import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const EMPTY = { position_type: '', from_date: '', to_date: '', details: '' };

function PositionModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    setForm(initial || EMPTY);
  }, [initial]);

  const submit = async () => {
    if (!form.position_type || !form.from_date) {
      toast.error('Position type and from date are required');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>{initial?.id ? 'Edit Position Type' : 'Add Position Type'}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Position Type *</label>
            <input className="form-input" value={form.position_type} onChange={set('position_type')} placeholder="e.g. Member , committee member" />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">From Date *</label>
              <input className="form-input" type="date" value={form.from_date} onChange={set('from_date')} />
            </div>
            <div className="form-group">
              <label className="form-label">To Date</label>
              <input className="form-input" type="date" value={form.to_date} onChange={set('to_date')} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Details</label>
            <textarea className="form-input" value={form.details} onChange={set('details')} placeholder="Additional details..." style={{ minHeight: 100 }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PositionType({ member }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoad] = useState(false);
  const [modal, setModal] = useState(null);
  const [deleting, setDel] = useState(null);

  useEffect(() => {
    if (!member?.id) return;
    setLoad(true);
    api.get(`/positions/${member.id}`)
      .then(r => setRows(r.data || []))
      .catch(() => toast.error('Failed to load positions'))
      .finally(() => setLoad(false));
  }, [member?.id]);

  const handleSave = async form => {
    try {
      if (modal.row?.id) {
        const { data } = await api.put(`/positions/${modal.row.id}`, form);
        setRows(p => p.map(r => r.id === data.id ? data : r));
        toast.success('Updated');
      } else {
        const { data } = await api.post('/positions', { member_id: member.id, ...form });
        setRows(p => [...p, data]);
        toast.success('Added');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this position?')) return;
    setDel(id);
    try {
      await api.delete(`/positions/${id}`);
      setRows(p => p.filter(r => r.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    } finally {
      setDel(null);
    }
  };

  if (!member?.id) return (
    <div className="empty-state">
      <p>Save Personal info first to manage position types.</p>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => setModal({ row: null })}>
          + Add Position
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text3)', textAlign: 'center', padding: 32 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div className="empty-state"><p>No positions added yet.</p></div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table>
            <thead>
              <tr>
                <th>Position</th>
                <th>From</th>
                <th>To</th>
                <th>Details</th>
                <th style={{ width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td><strong>{row.position_type}</strong></td>
                  <td>{row.from_date}</td>
                  <td>{row.to_date || '—'}</td>
                  <td style={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.details || '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setModal({ row })}>Edit</button>
                      <button className="btn btn-danger btn-sm" disabled={deleting === row.id}
                        onClick={() => handleDelete(row.id)}>
                        {deleting === row.id ? '…' : 'Del'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal !== null && (
        <PositionModal
          initial={modal.row}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
